import { useEffect } from "react";
import {
  Nav,
  Hero,
  LogoStripV2,
  DevelopmentOverview,
  HeroVideoV2,
  MarketingPinnedV5,
  CreativeCapabilitiesV5,
  CaseStudiesV5,
  TestimonialsV5,
  FAQ,
  ClosingV5,
  Blog,
  Footer,
  EditorialCollageV5,
  MediaBackgroundV5,
  ServicesGridV5Vertical,
} from "../App";
import { useSeo } from "../lib/useSeo";

/**
 * HomeV5 — visual-richness variant.
 * Built on top of V2. Adds stock-media blocks (images + video)
 * at strategic points to break up text-heavy sections and add
 * rhythm. Layout phase only — all media is stock placeholder.
 */
export default function HomeV5() {
  useSeo({
    route: "/",
    fallback: {
      title: "Sunday Digital — Shopify Plus Studio",
      description:
        "A boutique studio building, marketing, and scaling premium eCommerce brands on Shopify Plus.",
    },
  });

  // Editorial Reveal System — observe every [data-reveal] element on V5 and
  // toggle `is-revealed` when it scrolls into view. CSS handles the actual
  // animation (mask wipe / clip-path / fade). Once revealed, we unobserve so
  // re-entries don't replay.
  //
  // We ALSO need to handle elements that are added to the DOM AFTER initial
  // mount (e.g. CMS-driven sections that conditionally render once `fetch`
  // resolves — Editorial Collage tiles, Showcase galleries, etc.). A
  // MutationObserver watches the tree and registers any new [data-reveal]
  // descendant with the IntersectionObserver.
  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;

    // Fallback: if IntersectionObserver isn't available, just reveal
    // everything immediately.
    if (typeof IntersectionObserver === "undefined") {
      root.querySelectorAll("[data-reveal]").forEach((el) =>
        el.classList.add("is-revealed")
      );
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );

    const observe = (el) => {
      if (!el || el.classList.contains("is-revealed")) return;
      // If the element is already past the viewport top by the time it
      // mounts (true for late-arriving Editorial Collage tiles when the
      // user scrolls in fast or refreshes mid-page), reveal it
      // immediately so it doesn't sit invisible forever.
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const partiallyVisible = rect.top < vh && rect.bottom > 0;
      if (partiallyVisible) {
        el.classList.add("is-revealed");
        return;
      }
      io.observe(el);
    };

    // Observe everything that exists at mount.
    root.querySelectorAll("[data-reveal]").forEach(observe);

    // Watch for late-arriving children and observe them too. This is the
    // safety net for CMS-driven content that mounts after fetch resolves.
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.hasAttribute && node.hasAttribute("data-reveal")) {
            observe(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll("[data-reveal]").forEach(observe);
          }
        });
      });
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, []);

  return (
    <div
      data-testid="home-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <Hero ambientProps={{ drift: true }} reveal />
        <LogoStripV2 />

        {/* NEW — sticky-text + asymmetric tile collage (replaces 2-row marquee) */}
        <EditorialCollageV5 />

        <DevelopmentOverview />
        <HeroVideoV2 engageOnScroll />

        {/* "Our Shopify Services" — V5 keeps the vertical accordion only */}
        <ServicesGridV5Vertical />

        {/* NEW — blurred background video section with overlay copy */}
        <MediaBackgroundV5 />

        <MarketingPinnedV5 />

        <CreativeCapabilitiesV5 />

        <CaseStudiesV5 />
        <TestimonialsV5 />
        <FAQ />
        <ClosingV5 />
        <Blog />
      </main>
      <Footer />
    </div>
  );
}
